import React, { useState, useEffect } from "react";
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import styles from './simpleMap.module.css';
import { Boundary } from "./polyline";
import { ClusteredMarkers } from "./cluster";

type propsMapWrapper = {
  onMouseEnter: any,
  onMouseLeave: any,
  bidPage: boolean,
  results: resultsFormated[],
  boundary: {
    coordinates: Coordiantes
  } | null,
  center: {
    lat: number,
    lng: number
  },
  zoom: number,
  theme: {
    primary: string,
    secondary: string
  } | null
}

type resultsFormated = {
  lat: number,
  lng: number,
  name: string,
  id: number,
  item: {
    id: number | { raw: number };
    thumbnail_uri: string;
    address: any;
    name: string;
    resultHref: string;
    category_code: string;
  };
}

type Coordiantes = {
  bound: string[]
}

type TriangleCoords = {
  lat: string,
  lng: string
}

const MapWrapper = (props: propsMapWrapper) => {
  const [showInfo, setShowInfo] = useState(true);
  const { onMouseEnter, onMouseLeave } = props;
  useEffect(() => {
    if (typeof window !== 'undefined' && 'TouchEvent' in window) {
      const previousPreventDefault = TouchEvent.prototype.preventDefault;
      TouchEvent.prototype.preventDefault = function () {
        if (this.cancelable) {
          previousPreventDefault.call(this);
        }
      }
    }
  })

  const triangleCoords: TriangleCoords[] = [];
  if (props.bidPage && props.boundary) {
    props.boundary.coordinates[0].forEach((bound) => {
      triangleCoords.push({ lat: bound[1], lng: bound[0] })
    })
  }

  const fitBoundsOnLoad = (map) => {
    let markersData = props.results;

    if (markersData.length > 0) {
      let bounds = new google.maps.LatLngBounds();
      markersData.forEach((marker) => {
        bounds.extend(
          new google.maps.LatLng(marker.lat, marker.lng)
        );
      });

      if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
        let extendPoint1 = new google.maps.LatLng(bounds.getNorthEast().lat() + 0.01, bounds.getNorthEast().lng() + 0.001);
        let extendPoint2 = new google.maps.LatLng(bounds.getNorthEast().lat() - 0.01, bounds.getNorthEast().lng() - 0.001);
        bounds.extend(extendPoint1);
        bounds.extend(extendPoint2);
      }


      map.fitBounds(
        {
          north: bounds.getNorthEast().lat(),
          south: bounds.getSouthWest().lat(),
          east: bounds.getNorthEast().lng(),
          west: bounds.getSouthWest().lng()
        }
      )
    }

  }
  const map = useMap();
  useEffect(() => {
    if (!map || props.bidPage) return;
    fitBoundsOnLoad(map)

  }, [map]);


  return (
    <div className={`${styles.MapContainer} resultMap`} id="mapContainer">
      <Map
        mapId={'google-map-script'}
        defaultZoom={props.center.lat === null ? 6 : props.zoom}
        defaultCenter={props.center.lat === null ? { lat: 54.2361, lng: -4.5481 } : props.center}
        gestureHandling={'greedy'}
      >

        {/* simple marker */}
        <Boundary
          options={{
            strokeColor: props?.theme?.primary,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: props?.theme?.secondary,
            fillOpacity: 0.35
          }}
          boundary={triangleCoords}
        />

        <ClusteredMarkers results={props.results} setShowInfo={setShowInfo} showInfo={showInfo} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />

      </Map>
    </div >
  )
}

type Props = {
  lat: number,
  lng: number,
  zoom?: number,
  bidPage?: boolean,
  boundary?: {
    coordinates: Coordiantes
  } | null,
  theme?: {
    primary: string,
    secondary: string
  } | null,
  locationDetails: object | null,
  results: initalResults[]
}

type initalResults = {
  geo_location: {
    lat: string,
    lon: string,
  },
  name: string,
  full_name: string,
  id: number
  address: any;
  resultHref: string;
  category_code: string;
  thumbnail_uri: string;
}

export default function SimpleMap({ lat = 10.99835602, lng = 77.01502627, zoom = 12, results, bidPage = false, boundary = null, theme = null, locationDetails = null }: Props) {
  const defaultProps = {
    center: {
      lat,
      lng
    },
    zoom
  };

  const geoRes = results
    .map((r) => (
      { item: r, lat: parseFloat(r.geo_location?.lat), lng: parseFloat(r.geo_location?.lon), name: r.name ? r.name : r.full_name, id: r.id })
    )
    .filter((g) => typeof g.name === 'string')
    ;

  const onMouseEnter = (item) => {
    if (!bidPage) {
      handleToolKitPosition(item)
    }
    let elem = document.getElementById(`marker-${typeof item.id === 'object' ? item.id.raw : item.id}`);
    if (elem) {
      if (elem.parentElement && elem.parentElement.parentElement && elem.parentElement.parentElement.parentElement) {
        elem.parentElement.parentElement.parentElement.style.zIndex = '4000000'
      }
      elem.classList.add("focussed");
    }
  }

  const handleToolKitPosition = (item) => {
    const map = document.querySelector("#mapContainer");
    const mapBound = map?.getBoundingClientRect();
    const toolkit = document.querySelector(`#tootlKit-${item.id}`) as HTMLElement
    const toolKitBound = toolkit.getBoundingClientRect();

    if (toolkit && mapBound) {
      if (toolKitBound.x < 150) {
        toolkit.style.left = '39%';
        toolkit.style.right = 'auto';
      } else if ((toolKitBound.x + toolKitBound.width + 20) > (mapBound.x + mapBound.width)) {
        toolkit.style.left = 'auto';
        toolkit.style.right = '60%';
      }

      if ((toolKitBound.y + toolKitBound.height) > (mapBound.y + mapBound.height)) {
        toolkit.style.bottom = '100%';
        toolkit.style.top = 'auto';
      }
    }
  }

  const resetPosition = (item) => {
    const toolkit = document.querySelector(`#tootlKit-${item}`) as HTMLElement;
    // sometimes we don't find the item
    if (!toolkit) {
      return
    }

    toolkit.style.left = 'auto';
    toolkit.style.right = '-150%';
    toolkit.style.top = '99%';
    toolkit.style.bottom = 'auto';
  }

  const onMouseLeave = (item) => {
    if (!bidPage) {
      resetPosition(item)
    }

    let elem = document.getElementById(`marker-${item}`);
    if (elem) {
      if (elem.parentElement && elem.parentElement.parentElement && elem.parentElement.parentElement.parentElement) {
        elem.parentElement.parentElement.parentElement.style.zIndex = '1'
      }
      elem.classList.remove("focussed");
    }
  }

  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_APIS_KEY_CSR ?? ''

  return (
    // Important! Always set the container height explicitly
    // <div className="resultMap" style={{ height: '80vh', width: '100%' }}>
    <APIProvider apiKey={apiKey} libraries={['marker']} >
      <MapWrapper {...defaultProps} results={geoRes} boundary={boundary} bidPage={bidPage} theme={theme} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />
    </APIProvider>
  );
}
